import { BALANCE } from '../balance.ts';
import type { ActionError, PlayerId, PlayerState, Shares } from '../types.ts';
import { emit, fail, mustPlayer, type Ctx } from './context.ts';
import { setPhase } from './core.ts';
import { computeResult, equalShares } from './outcome.ts';

/** The hideout (GDD §5.6): discussion with pins, secret accusation, then the split. */

export function startHideout(ctx: Ctx): void {
  const s = ctx.s;
  setPhase(ctx, 'hideout');
  s.hideout = {
    stage: 'discussion',
    stageStartedMs: s.clockMs,
    accusations: {},
    expelled: null,
    accusationResolved: false,
    proposer: null,
    proposal: null,
    splitVotes: {},
    splitRejections: 0,
    splitVoting: false,
    finalShares: null,
    equalSplit: false,
  };
  emit(ctx, 'hideout.stage', { stage: 'discussion' });
}

const eligible = (ctx: Ctx): PlayerState[] => ctx.s.players.filter((p) => !p.expelled);

// ---- Discussion ------------------------------------------------------------------------

export function placePin(ctx: Ctx, by: PlayerId, target: PlayerId | null): ActionError | null {
  const s = ctx.s;
  const h = s.hideout;
  if (s.phase !== 'hideout' || !h || h.stage !== 'discussion') {
    return fail('wrongPhase', 'pins are for the discussion');
  }
  if (target !== null) {
    if (target === by) return fail('invalid', 'you cannot pin yourself');
    if (!s.players.some((p) => p.id === target)) return fail('invalid', 'no such player');
  }
  mustPlayer(s, by).pin = target;
  emit(ctx, 'pin.placed', { by, target });
  return null;
}

// ---- Accusation ------------------------------------------------------------------------

export function accuse(ctx: Ctx, by: PlayerId, target: PlayerId | 'none'): ActionError | null {
  const s = ctx.s;
  const h = s.hideout;
  if (s.phase !== 'hideout' || !h || h.stage !== 'accusation') {
    return fail('wrongPhase', 'it is not time to accuse');
  }
  if (by in h.accusations) return fail('invalid', 'you already voted');
  if (target !== 'none') {
    if (target === by) return fail('invalid', 'you cannot accuse yourself');
    if (!s.players.some((p) => p.id === target)) return fail('invalid', 'no such player');
  }
  h.accusations[by] = target;
  // Secret ballot: everyone learns that a vote was cast, nobody learns what it was.
  emit(ctx, 'accuse.voted', { by });
  if (Object.keys(h.accusations).length >= s.players.length) resolveAccusation(ctx);
  return null;
}

function resolveAccusation(ctx: Ctx): void {
  const s = ctx.s;
  const h = s.hideout;
  if (!h || h.accusationResolved) return;
  const counts: Record<string, number> = {};
  for (const p of s.players) {
    const v = h.accusations[p.id] ?? 'none';
    if (v !== 'none') counts[v] = (counts[v] ?? 0) + 1;
  }
  const need = Math.floor(s.players.length / 2) + 1;
  const top = Object.entries(counts).find(([, n]) => n >= need);
  h.accusationResolved = true;
  h.expelled = top ? top[0] : null;
  if (h.expelled) mustPlayer(s, h.expelled).expelled = true;
  emit(ctx, 'accusation.result', { expelled: h.expelled, counts });
  startSplit(ctx);
}

// ---- Split -----------------------------------------------------------------------------

function startSplit(ctx: Ctx): void {
  const s = ctx.s;
  const h = s.hideout;
  if (!h) return;
  h.stage = 'split';
  h.stageStartedMs = s.clockMs;
  const mastermind = s.players.find((p) => p.role === 'mastermind') as PlayerState;
  h.proposer = mastermind.expelled ? nextProposer(ctx, mastermind.id) : mastermind.id;
  emit(ctx, 'hideout.stage', { stage: 'split', proposer: h.proposer });
}

/** The next non-expelled player after `from`, in seat order. */
function nextProposer(ctx: Ctx, from: PlayerId): PlayerId {
  const seats = [...ctx.s.players].sort((a, b) => a.seat - b.seat);
  const start = seats.findIndex((p) => p.id === from);
  for (let i = 1; i <= seats.length; i++) {
    const p = seats[(start + i) % seats.length] as PlayerState;
    if (!p.expelled) return p.id;
  }
  return from;
}

export function proposeSplit(ctx: Ctx, by: PlayerId, shares: Shares): ActionError | null {
  const s = ctx.s;
  const h = s.hideout;
  if (s.phase !== 'hideout' || !h || h.stage !== 'split' || h.splitVoting) {
    return fail('wrongPhase', 'no split proposal is expected now');
  }
  if (h.proposer !== by) return fail('notAllowed', 'it is not your turn to propose');
  const ids = eligible(ctx).map((p) => p.id);
  const given = Object.keys(shares);
  if (given.length !== ids.length || !ids.every((id) => id in shares)) {
    return fail('invalid', 'shares must name exactly the players who are still in');
  }
  let total = 0;
  for (const id of ids) {
    const v = shares[id] as number;
    if (!Number.isInteger(v) || v < 0 || v > 100)
      return fail('invalid', 'shares are whole percentages');
    total += v;
  }
  if (total !== 100) return fail('invalid', 'shares must add up to 100');
  h.proposal = { ...shares };
  h.splitVoting = true;
  h.splitVotes = {};
  h.stageStartedMs = s.clockMs;
  emit(ctx, 'split.proposed', { by, shares: h.proposal });
  return null;
}

export function voteSplit(ctx: Ctx, by: PlayerId, approve: boolean): ActionError | null {
  const s = ctx.s;
  const h = s.hideout;
  if (s.phase !== 'hideout' || !h || h.stage !== 'split' || !h.splitVoting) {
    return fail('wrongPhase', 'there is no split to vote on');
  }
  if (mustPlayer(s, by).expelled) return fail('notAllowed', 'you are out');
  if (by in h.splitVotes) return fail('invalid', 'you already voted');
  h.splitVotes[by] = approve;
  emit(ctx, 'split.voted', { by });
  if (Object.keys(h.splitVotes).length >= eligible(ctx).length) resolveSplitVote(ctx);
  return null;
}

function resolveSplitVote(ctx: Ctx): void {
  const s = ctx.s;
  const h = s.hideout;
  if (!h || !h.proposal) return;
  const voters = eligible(ctx);
  const yes = voters.filter((p) => h.splitVotes[p.id] === true).length;
  const approved = yes * 2 > voters.length;
  emit(ctx, 'split.result', { approved, yes, no: voters.length - yes });
  if (approved) {
    finish(ctx, h.proposal);
    return;
  }
  rejectSplit(ctx);
}

function rejectSplit(ctx: Ctx): void {
  const s = ctx.s;
  const h = s.hideout;
  if (!h) return;
  h.splitRejections += 1;
  h.proposal = null;
  h.splitVoting = false;
  h.splitVotes = {};
  if (h.splitRejections >= BALANCE.maxSplitRejections) {
    h.equalSplit = true;
    emit(ctx, 'split.equal', {});
    finish(ctx, equalShares(eligible(ctx).map((p) => p.id)));
    return;
  }
  h.proposer = nextProposer(ctx, h.proposer as PlayerId);
  h.stageStartedMs = s.clockMs;
  emit(ctx, 'split.next', { proposer: h.proposer, rejections: h.splitRejections });
}

function finish(ctx: Ctx, shares: Shares): void {
  const s = ctx.s;
  const h = s.hideout;
  if (!h) return;
  h.finalShares = { ...shares };
  s.result = computeResult(s, true, null);
  emit(ctx, 'heist.completed', {});
  setPhase(ctx, 'reveal');
}

// ---- Time ------------------------------------------------------------------------------

export function tickHideout(ctx: Ctx): void {
  const s = ctx.s;
  const h = s.hideout;
  if (s.phase !== 'hideout' || !h) return;
  const elapsed = s.clockMs - h.stageStartedMs;
  if (h.stage === 'discussion' && elapsed >= BALANCE.discussionMs) {
    h.stage = 'accusation';
    h.stageStartedMs = s.clockMs;
    emit(ctx, 'hideout.stage', { stage: 'accusation' });
  } else if (h.stage === 'accusation' && elapsed >= BALANCE.accusationMs) {
    resolveAccusation(ctx);
  } else if (h.stage === 'split') {
    if (h.splitVoting && elapsed >= BALANCE.splitVoteMs) resolveSplitVote(ctx);
    else if (!h.splitVoting && elapsed >= BALANCE.splitProposeMs) {
      // No proposal in time counts as a rejection (GDD §5.6).
      emit(ctx, 'split.result', { approved: false, yes: 0, no: 0, timeout: true });
      rejectSplit(ctx);
    }
  }
}

export function tickReveal(ctx: Ctx): void {
  const s = ctx.s;
  if (s.phase === 'reveal' && s.clockMs - s.phaseStartedMs >= BALANCE.revealMs) endGame(ctx);
}

export function endGame(ctx: Ctx): void {
  if (ctx.s.phase !== 'reveal') return;
  setPhase(ctx, 'ended');
}
